/**
 * Voting Threshold Detection System
 * Based on Robert's Rules of Order - determines required voting thresholds
 */

/**
 * Voting threshold types
 */
export const VOTE_THRESHOLDS = {
  MAJORITY: 'majority',                    // More than half of votes cast
  TWO_THIRDS: 'two_thirds',               // At least 2/3 of votes cast
  MAJORITY_WITH_NOTICE: 'majority_with_notice',  // Majority when previous notice given
  MAJORITY_OF_MEMBERSHIP: 'majority_of_membership'  // Majority of entire membership
};

/**
 * Motion-specific voting requirements
 * Based on Robert's Rules Meeting Assistant Guide
 */
const MOTION_VOTE_REQUIREMENTS = {
  // MAIN MOTIONS
  'MAIN': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },

  // SUBSIDIARY MOTIONS
  'POSTPONE_INDEFINITELY': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  // Alias for in-app motion key
  'POSTPONE_INDEF': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'AMEND': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'COMMIT': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'POSTPONE': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'LIMIT_DEBATE': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (protects minority rights)'
  },
  'PREVIOUS_QUESTION': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (limits debate)'
  },
  // Aliases for in-app motion keys (src/utils/motionTypes.js)
  'PREVQ': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (limits debate)'
  },
  'TABLE': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },

  // PRIVILEGED MOTIONS
  'RECESS': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'ADJOURN': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'FIX_TIME_ADJOURN': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  // Alias for in-app motion key
  'FIX_HOUR': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },

  // INCIDENTAL MOTIONS
  'APPEAL': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast (tie vote sustains chair)'
  },
  'SUSPEND_RULES': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (protects organizational rules)'
  },
  'OBJECT_CONSIDERATION': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (prevents discussion)'
  },
  'DIVISION_QUESTION': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },

  // MOTIONS THAT BRING BACK BUSINESS
  'RECONSIDER': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'TAKE_FROM_TABLE': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'RATIFY': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast'
  },
  'RESCIND': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (without notice)',
    alternativeThreshold: VOTE_THRESHOLDS.MAJORITY_WITH_NOTICE,
    alternativeDescription: 'Majority of votes cast (with previous notice)'
  },
  'AMEND_PREVIOUS': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (without notice)',
    alternativeThreshold: VOTE_THRESHOLDS.MAJORITY_WITH_NOTICE,
    alternativeDescription: 'Majority of votes cast (with previous notice)'
  },

  // NOMINATIONS AND ELECTIONS
  'ELECTION': {
    threshold: VOTE_THRESHOLDS.MAJORITY,
    description: 'Majority of votes cast (unless bylaws specify plurality)'
  },

  // SPECIAL PROCEDURES
  'AMEND_BYLAWS': {
    threshold: VOTE_THRESHOLDS.TWO_THIRDS,
    description: 'Two-thirds of votes cast (with previous notice)',
    requiresNotice: true
  }
};

/**
 * Calculate the required number of votes for a given threshold
 * @param {string} threshold - The threshold type
 * @param {number} votesCast - Number of votes actually cast
 * @param {number} totalMembership - Total membership (for majority of membership)
 * @returns {Object} Required votes and details
 */
export function calculateRequiredVotes(threshold, votesCast, totalMembership = null) {
  let requiredVotes;
  let description;

  switch (threshold) {
    case VOTE_THRESHOLDS.MAJORITY:
      requiredVotes = Math.floor(votesCast / 2) + 1;
      description = `More than half of ${votesCast} votes cast (${requiredVotes} votes needed)`;
      break;

    case VOTE_THRESHOLDS.TWO_THIRDS:
      requiredVotes = Math.ceil(votesCast * 2 / 3);
      description = `At least two-thirds of ${votesCast} votes cast (${requiredVotes} votes needed)`;
      break;

    case VOTE_THRESHOLDS.MAJORITY_WITH_NOTICE:
      requiredVotes = Math.floor(votesCast / 2) + 1;
      description = `More than half of ${votesCast} votes cast with previous notice (${requiredVotes} votes needed)`;
      break;

    case VOTE_THRESHOLDS.MAJORITY_OF_MEMBERSHIP:
      if (!totalMembership) {
        throw new Error('Total membership required for majority of membership calculation');
      }
      requiredVotes = Math.floor(totalMembership / 2) + 1;
      description = `More than half of ${totalMembership} total members (${requiredVotes} votes needed)`;
      break;

    default:
      requiredVotes = Math.floor(votesCast / 2) + 1;
      description = `Majority of votes cast (${requiredVotes} votes needed)`;
  }

  return {
    requiredVotes,
    threshold,
    description,
    votesCast,
    totalMembership
  };
}

/**
 * Get voting requirements for a specific motion type
 * @param {string} motionType - The motion type key
 * @param {boolean} hasNotice - Whether previous notice was given
 * @returns {Object} Voting requirements for the motion
 */
export function getMotionVotingRequirements(motionType, hasNotice = false) {
  const requirements = MOTION_VOTE_REQUIREMENTS[motionType];

  if (!requirements) {
    // Default to majority vote for unknown motions
    return {
      threshold: VOTE_THRESHOLDS.MAJORITY,
      description: 'Majority of votes cast (default)',
      hasAlternative: false
    };
  }

  // Handle motions with alternative requirements based on notice
  if (requirements.alternativeThreshold && hasNotice) {
    return {
      threshold: requirements.alternativeThreshold,
      description: requirements.alternativeDescription,
      hasAlternative: true,
      primaryThreshold: requirements.threshold,
      primaryDescription: requirements.description
    };
  }

  return {
    threshold: requirements.threshold,
    description: requirements.description,
    hasAlternative: !!requirements.alternativeThreshold,
    alternativeThreshold: requirements.alternativeThreshold,
    alternativeDescription: requirements.alternativeDescription,
    requiresNotice: requirements.requiresNotice || false
  };
}

/**
 * Determine if a vote passes based on motion type and vote counts
 * @param {string} motionType - The motion type
 * @param {number} yesVotes - Number of yes votes
 * @param {number} noVotes - Number of no votes
 * @param {number} totalMembership - Total membership (optional)
 * @param {boolean} hasNotice - Whether previous notice was given
 * @returns {Object} Vote result analysis
 */
export function analyzeVoteResult(motionType, yesVotes, noVotes, totalMembership = null, hasNotice = false) {
  const votesCast = yesVotes + noVotes;
  const requirements = getMotionVotingRequirements(motionType, hasNotice);
  const calculation = calculateRequiredVotes(requirements.threshold, votesCast, totalMembership);

  const passes = yesVotes >= calculation.requiredVotes;

  return {
    passes,
    yesVotes,
    noVotes,
    votesCast,
    requiredVotes: calculation.requiredVotes,
    threshold: requirements.threshold,
    description: requirements.description,
    margin: yesVotes - calculation.requiredVotes,
    percentage: votesCast > 0 ? ((yesVotes / votesCast) * 100).toFixed(1) : 0,
    hasAlternative: requirements.hasAlternative,
    alternativeThreshold: requirements.alternativeThreshold,
    alternativeDescription: requirements.alternativeDescription
  };
}

/**
 * Get smart voting guidance for the chair
 * @param {string} motionType - The motion type
 * @param {boolean} hasNotice - Whether previous notice was given
 * @returns {Object} Guidance for conducting the vote
 */
export function getVotingGuidance(motionType, hasNotice = false) {
  const requirements = getMotionVotingRequirements(motionType, hasNotice);

  let votingMethod = 'voice'; // Default
  let guidance = 'Take a voice vote ("All in favor say aye... Opposed, no")';

  // Recommend rising vote for 2/3 votes for accuracy
  if (requirements.threshold === VOTE_THRESHOLDS.TWO_THIRDS) {
    votingMethod = 'rising';
    guidance = 'Take a rising vote for accuracy ("All in favor please rise... Be seated. Those opposed please rise")';
  }

  return {
    motionType,
    threshold: requirements.threshold,
    description: requirements.description,
    votingMethod,
    guidance,
    requiresNotice: requirements.requiresNotice,
    hasAlternative: requirements.hasAlternative,
    alternativeOption: requirements.hasAlternative ? {
      threshold: requirements.alternativeThreshold,
      description: requirements.alternativeDescription
    } : null
  };
}

/**
 * Check if a motion requires previous notice
 * @param {string} motionType - The motion type
 * @returns {boolean} Whether the motion requires previous notice
 */
export function requiresPreviousNotice(motionType) {
  const requirements = MOTION_VOTE_REQUIREMENTS[motionType];
  return requirements?.requiresNotice || false;
}